/**
 * Study member operations — student and teacher CRUD within groups, assignment helpers.
 */

import type { StudyGroup, Student, Teacher, StudentAssignment } from '@skjv/common';
import type { GroupStoreDeps } from './study-group-ops';

// ============================================================
// Student CRUD (within groups)
// ============================================================

/** Add a student to a group */
export function addStudentToGroup(
  deps: GroupStoreDeps,
  groupId: string,
  student: Omit<Student, 'id'>,
): string | null {
  const group = deps.studyGroups.find((g) => g.id === groupId);
  if (!group) return null;

  const id = deps.generateId();
  const newStudent: Student = { id, ...student };

  deps.studyGroups = deps.studyGroups.map((g) =>
    g.id === groupId ? { ...g, students: [...g.students, newStudent], updatedAt: deps.now() } : g,
  );
  deps.saveStudyGroups();
  return id;
}

/** Update a student in a group */
export function updateStudent(
  deps: GroupStoreDeps,
  groupId: string,
  studentId: string,
  updates: Partial<Omit<Student, 'id'>>,
): void {
  deps.studyGroups = deps.studyGroups.map((g) =>
    g.id === groupId
      ? {
          ...g,
          students: g.students.map((s) => (s.id === studentId ? { ...s, ...updates } : s)),
          updatedAt: deps.now(),
        }
      : g,
  );
  deps.saveStudyGroups();
}

/** Remove a student from a group */
export function removeStudentFromGroup(
  deps: GroupStoreDeps,
  groupId: string,
  studentId: string,
): void {
  deps.studyGroups = deps.studyGroups.map((g) =>
    g.id === groupId
      ? { ...g, students: g.students.filter((s) => s.id !== studentId), updatedAt: deps.now() }
      : g,
  );
  deps.saveStudyGroups();
}

// ============================================================
// Teacher CRUD (within groups)
// ============================================================

/** Add a teacher to a group */
export function addTeacherToGroup(
  deps: GroupStoreDeps,
  groupId: string,
  name: string,
): string | null {
  const group = deps.studyGroups.find((g) => g.id === groupId);
  if (!group) return null;

  const id = deps.generateId();
  const teacher: Teacher = { id, name };
  const teachers = [...(group.teachers || []), teacher];

  deps.studyGroups = deps.studyGroups.map((g) =>
    g.id === groupId ? { ...g, teachers, updatedAt: deps.now() } : g,
  );
  deps.saveStudyGroups();
  return id;
}

/** Remove a teacher from a group */
export function removeTeacherFromGroup(
  deps: GroupStoreDeps,
  groupId: string,
  teacherId: string,
): void {
  deps.studyGroups = deps.studyGroups.map((g) =>
    g.id === groupId
      ? {
          ...g,
          teachers: (g.teachers || []).filter((t) => t.id !== teacherId),
          updatedAt: deps.now(),
        }
      : g,
  );
  deps.saveStudyGroups();
}

// ============================================================
// Assignment helpers
// ============================================================

/** Create assignments for all students in a group */
export function createAssignmentsForGroup(
  studyGroups: StudyGroup[],
  groupId: string,
): StudentAssignment[] {
  const group = studyGroups.find((g) => g.id === groupId);
  if (!group) return [];

  return group.students.map((student) => ({
    student,
    reviewQuestion: null,
    startingLocation: undefined,
    endingLocation: undefined,
  }));
}
